import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axios';
import './NewGroupForm.css';

/** ───────────────── helpers ───────────────── **/
const LS_LOCALE_KEY = 'locale';
const FALLBACK_LOCALE = 'kz';
const readLocale = () => localStorage.getItem(LS_LOCALE_KEY) || FALLBACK_LOCALE;

function useReactiveLocale(intervalMs = 300) {
  const [locale, setLocale] = useState(readLocale());
  const prevRef = useRef(locale);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS_LOCALE_KEY) {
        const next = e.newValue || FALLBACK_LOCALE;
        prevRef.current = next;
        setLocale(next);
      }
    };
    window.addEventListener('storage', onStorage);

    const onCustom = (e) => {
      const next = e?.detail?.locale || readLocale();
      prevRef.current = next;
      setLocale(next);
    };
    window.addEventListener('app:locale-changed', onCustom);

    const id = setInterval(() => {
      const cur = readLocale();
      if (cur !== prevRef.current) {
        prevRef.current = cur;
        setLocale(cur);
      }
    }, intervalMs);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('app:locale-changed', onCustom);
      clearInterval(id);
    };
  }, [intervalMs]);

  return locale;
}

export default function NewGroupForm() {
  const locale = useReactiveLocale();

  const t = useMemo(
    () =>
      ({
        kz: {
          createGroup: 'Жаңа ОӘК құру',
          subject: 'Пән',
          select: 'Таңдаңыз',
          categories: 'Санаттарды таңдаңыз',
          educationLevel: 'Білім беру деңгейі',
          groupName: 'ОӘК атауы',
          nameExample: 'Мысалы: 5-сынып, Қазақ тілі пәні',
          description: 'Сипаттама (міндетті емес)',
          descriptionPlaceholder: 'Қосымша мәлімет',
          create: 'Құру',
          success: '✅ Топ сәтті құрылды',
          error: '❌ Қате орын алды',
          fetchError: 'Сілтеме қатесі:',
          searchCat: 'Санатты іздеу…',
          onlySelected: 'Тек таңдалғандарды көрсету',
          chooseAll: 'Барлығын таңдау',
          clear: 'Тазалау',
          chosen: 'Таңдалған:',
          nothingFound: 'Ештеңе табылмады',
          successTitle: 'Сәтті!',
          errorTitle: 'Қате',
          grade: 'Сынып',
          direction: 'Бағыт',
          devLang: 'Әзірлеу тілі',
          teachLang: 'Оқыту тілі',
          publicationType: 'Басылым түрі',
          serviceType: 'Қызмет түрі',
          submissionYear: 'Өтінім жылы',
          autoFill: 'Авто',
          autoHint: 'Авто атауды қайта қою',
          parentGroup: 'Алдыңғы топты таңдаңыз',
          parentGroupLabel: 'Алдыңғы топ (доработка)',
          parentGroupHint: 'Өңдеуден өткен топты таңдаңыз — деректері автоматты көшіріледі',
          parentGroupLoading: 'Жүктелуде...',
          parentGroupEmpty: 'Аяқталған топтар табылмады',
          copiedInfo: '✅ Алдыңғы топтың деректері автоматты толтырылды',
        },
        ru: {
          createGroup: 'Создание новой УМК',
          subject: 'Предмет',
          select: 'Выберите',
          categories: 'Выберите категории',
          educationLevel: 'Уровень образования',
          groupName: 'Название УМК',
          nameExample: 'Например: 5 класс, Каз. язык',
          description: 'Описание (необязательно)',
          descriptionPlaceholder: 'Дополнительная информация',
          create: 'Создать',
          success: '✅ Группа успешно создана',
          error: '❌ Произошла ошибка',
          fetchError: 'Ошибка получения справочников:',
          searchCat: 'Поиск категории…',
          onlySelected: 'Только выбранные',
          chooseAll: 'Выбрать все',
          clear: 'Очистить',
          chosen: 'Выбрано:',
          nothingFound: 'Ничего не найдено',
          successTitle: 'Успешно!',
          errorTitle: 'Ошибка',
          grade: 'Класс',
          direction: 'Направление',
          devLang: 'Язык разработки',
          teachLang: 'Язык обучения',
          publicationType: 'Тип издания',
          serviceType: 'Тип услуги',
          submissionYear: 'Год подачи',
          autoFill: 'Авто',
          autoHint: 'Вернуть автозаголовок',
          parentGroup: 'Выберите предыдущую группу',
          parentGroupLabel: 'Предыдущая группа (доработка)',
          parentGroupHint: 'Выберите завершённую группу — данные скопируются автоматически',
          parentGroupLoading: 'Загрузка...',
          parentGroupEmpty: 'Завершённых групп не найдено',
          copiedInfo: '✅ Данные предыдущей группы заполнены автоматически',
        },
      }[locale]),
    [locale]
  );

  const modeText = useMemo(
    () =>
      ({
        kz: {
          new: 'Ғылыми педагогикалық сараптама',
          cont: 'Өңдеуден кейінгі сараптама',
          hint: 'Сараптама түрін таңдаңыз',
          label: 'Сараптама түрі',
        },
        ru: {
          new: 'Научно-педагогическая экспертиза',
          cont: 'Экспертиза после доработки',
          hint: 'Выберите тип экспертизы',
          label: 'Тип экспертизы',
        },
      }[locale]),
    [locale]
  );

  /** ───────────────── state ───────────────── **/
  const [catQ, setCatQ] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);

  const [form, setForm] = useState({
    subject_id: '',
    category_ids: [],
    education_level_id: '',
    title: '',
    description: '',
    continuation_mode: 'new',
    parent_group_id: '',
    grade_id: '',
    direction_id: '',
    development_language_id: '',
    teaching_language_id: '',
    publication_type_id: '',
    service_type_id: '',
    submission_year_id: '',
  });

  const [refs, setRefs] = useState({
    subjects: [],
    categories: [],
    education_levels: [],
    grades: [],
    directions: [],
    languages: [],
    publication_types: [],
    service_types: [],
    years: [],
  });

  // Аяқталған топтар (continuation үшін)
  const [completedGroups, setCompletedGroups] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [selectedParentGroup, setSelectedParentGroup] = useState(null);

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const isContinuation = form.continuation_mode === 'continuation';
  const isPreSchool = form.education_level_id === '1';
  const isMode = (m) => form.continuation_mode === m;

  /** ───────────────── utils ───────────────── **/
  const toId = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const getRefName = (arr, id) => {
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) return '';
    const item = (arr || []).find((x) => Number(x.id) === n);
    if (!item) return '';
    return (item[`name_${locale}`] || item.name || '').trim();
  };

  const autoTitle = useMemo(() => {
    if (isContinuation && selectedParentGroup) {
      return selectedParentGroup.title || '';
    }
    const gradeName = getRefName(refs.grades, form.grade_id);
    const subjectName = getRefName(refs.subjects, form.subject_id);
    const eduName = getRefName(refs.education_levels, form.education_level_id);
    return [gradeName, subjectName, eduName].filter(Boolean).join(', ');
  }, [
    locale,
    isContinuation,
    selectedParentGroup,
    refs.grades,
    refs.subjects,
    refs.education_levels,
    form.grade_id,
    form.subject_id,
    form.education_level_id,
  ]);

  useEffect(() => {
    if (titleTouched) return;
    setForm((p) => ({ ...p, title: autoTitle }));
  }, [autoTitle, titleTouched]);

  /** ───────────────── fetch refs ───────────────── **/
  useEffect(() => {
    let alive = true;

    const fetchRefs = async () => {
      try {
        const headers = { 'Accept-Language': locale };

        const [
          subjectRes,
          categoryRes,
          educationRes,
          gradeRes,
          directionRes,
          languageRes,
          publicationTypeRes,
          serviceTypeRes,
          yearsRes,
        ] = await Promise.all([
          axios.get('/publisher/ref/subjects', { headers }),
          axios.get('/publisher/ref/categories', { headers }),
          axios.get('/publisher/ref/education-levels', { headers }),
          axios.get('/publisher/ref/grades', { headers }),
          axios.get('/publisher/ref/directions', { headers }),
          axios.get('/publisher/ref/languages', { headers }),
          axios.get('/publisher/ref/publication-types', { headers }),
          axios.get('/publisher/ref/service-types', { headers }),
          axios.get('/publisher/ref/years', { headers }),
        ]);

        if (!alive) return;

        setRefs({
          subjects: subjectRes.data || [],
          categories: categoryRes.data || [],
          education_levels: educationRes.data || [],
          grades: gradeRes.data || [],
          directions: directionRes.data || [],
          languages: languageRes.data || [],
          publication_types: publicationTypeRes.data || [],
          service_types: serviceTypeRes.data || [],
          years: yearsRes.data || [],
        });
      } catch (err) {
        console.error(t.fetchError, err);
      }
    };

    fetchRefs();
    return () => { alive = false; };
  }, [locale]);

  /** ───────────────── fetch completed groups (continuation үшін) ───────────────── **/
  useEffect(() => {
    if (!isContinuation) {
      setCompletedGroups([]);
      setSelectedParentGroup(null);
      setForm((p) => ({ ...p, parent_group_id: '' }));
      return;
    }

    setCompletedLoading(true);
    axios
      .get('/publisher/groups/completed', {
        headers: { 'Accept-Language': locale },
      })
      .then((res) => {
        setCompletedGroups(res.data?.data || []);
      })
      .catch((err) => {
        console.error('completedGroups fetch error:', err);
        setCompletedGroups([]);
      })
      .finally(() => setCompletedLoading(false));
  }, [isContinuation, locale]);

  /** ───────────────── parent group таңдалғанда деректерді көшіру ───────────────── **/
  const handleParentGroupSelect = (groupId) => {
    const group = completedGroups.find((g) => String(g.id) === String(groupId));

    setForm((p) => ({ ...p, parent_group_id: groupId }));
    setSelectedParentGroup(group || null);

    if (group) {
      // Алдыңғы топтың категорияларын автоматты толтыру
      const catIds = (group.categories || []).map((c) => String(c.id));
      setForm((p) => ({
        ...p,
        parent_group_id: groupId,
        category_ids: catIds,
      }));
      setTitleTouched(false); // Атауды автоматты жаңарту үшін
      setInfo(t.copiedInfo);
    } else {
      setInfo('');
    }
  };

  /** ───────────────── preschool rule ───────────────── **/
  useEffect(() => {
    if (isPreSchool && !isContinuation) {
      setForm((prev) => ({ ...prev, category_ids: ['2'] }));
      setInfo(
        locale === 'kz'
          ? 'Бұл білім деңгейі үшін тек "Оқу-әдістемелік кешен" қолданылады.'
          : 'Для этого уровня можно выбрать только "Учебно-методический комплекс"'
      );
    } else if (!isContinuation) {
      setInfo('');
    }
  }, [isPreSchool, locale, isContinuation]);

  /** ───────────────── handlers ───────────────── **/
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleTitleChange = (e) => {
    setTitleTouched(true);
    setForm((p) => ({ ...p, title: e.target.value }));
  };

  const resetTitleToAuto = () => {
    setTitleTouched(false);
    setForm((p) => ({ ...p, title: autoTitle }));
  };

  const handleCheckboxChange = (e) => {
    if (isContinuation) return; // continuation-да категориялар өзгертілмейді
    const value = String(e.target.value);
    setForm((prev) => ({
      ...prev,
      category_ids: prev.category_ids.includes(value)
        ? prev.category_ids.filter((id) => id !== value)
        : [...prev.category_ids, value],
    }));
  };

  const handleModeChange = (mode) => {
    setForm((p) => ({
      ...p,
      continuation_mode: mode,
      parent_group_id: '',
      // new режимге өтсе категорияларды тазалаймыз
      category_ids: mode === 'new' ? [] : p.category_ids,
    }));
    setSelectedParentGroup(null);
    setTitleTouched(false);
    setInfo('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let data;

      if (isContinuation) {
        // ── continuation режимі ──
        if (!form.parent_group_id) {
          setError(
            locale === 'kz'
              ? '❌ Алдыңғы топты таңдаңыз'
              : '❌ Выберите предыдущую группу'
          );
          setLoading(false);
          return;
        }

        data = {
          title: (form.title || '').trim(),
          description: (form.description || '').trim() || null,
          continuation_mode: 'continuation',
          parent_group_id: toId(form.parent_group_id),
        };
      } else {
        // ── new режимі ──
        data = {
          subject_id: toId(form.subject_id),
          education_level_id: toId(form.education_level_id),
          title: (form.title || '').trim(),
          description: (form.description || '').trim() || null,
          grade_id: toId(form.grade_id),
          direction_id: toId(form.direction_id),
          development_language_id: toId(form.development_language_id),
          teaching_language_id: toId(form.teaching_language_id),
          publication_type_id: toId(form.publication_type_id),
          service_type_id: toId(form.service_type_id),
          submission_year_id: toId(form.submission_year_id),
          category_ids: (form.category_ids || [])
            .map((id) => Number(id))
            .filter((x) => Number.isFinite(x)),
          continuation_mode: 'new',
        };
      }

      await axios.post('/publisher/groups', data, {
        headers: { 'Content-Type': 'application/json', 'Accept-Language': locale },
      });

      setSuccess(t.success);
      setError('');
      setTitleTouched(false);
      setSelectedParentGroup(null);
      setInfo('');

      setForm({
        subject_id: '',
        category_ids: [],
        education_level_id: '',
        title: '',
        description: '',
        continuation_mode: 'new',
        parent_group_id: '',
        grade_id: '',
        direction_id: '',
        development_language_id: '',
        teaching_language_id: '',
        publication_type_id: '',
        service_type_id: '',
        submission_year_id: '',
      });
    } catch (err) {
      console.error('Қате:', err?.response?.data || err?.message);
      const msg = err?.response?.data?.message || t.error;
      setError(msg);
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const dismissToast = () => {
    setSuccess('');
    setError('');
  };

  useEffect(() => {
    if (!success && !error) return;
    const id = setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
    return () => clearTimeout(id);
  }, [success, error]);

  /** ───────────────── render ───────────────── **/
  return (
    <div className="new-form">
      <form onSubmit={handleSubmit} className="styled-form">
        <h2>{t.createGroup}</h2>

        {/* ── Сараптама түрі ── */}
        <div className="ngf-group">
          <label className="ngf-label">{modeText.label}</label>
          <p className="ngf-hint">{modeText.hint}</p>

          <div className="ngf-dualswitch" role="radiogroup" aria-label={modeText.label}>
            <label className={`ngf-opt ngf-opt--new ${isMode('new') ? 'is-on' : ''}`}>
              <span className="ngf-opt__label">{modeText.new}</span>
              <input
                type="radio"
                name="continuation_mode"
                checked={isMode('new')}
                onChange={() => handleModeChange('new')}
              />
              <span className="ngf-toggle" aria-hidden="true">
                <span className="ngf-toggle__knob" />
              </span>
            </label>

            <label className={`ngf-opt ngf-opt--cont ${isMode('continuation') ? 'is-on' : ''}`}>
              <span className="ngf-opt__label">{modeText.cont}</span>
              <input
                type="radio"
                name="continuation_mode"
                checked={isMode('continuation')}
                onChange={() => handleModeChange('continuation')}
              />
              <span className="ngf-toggle" aria-hidden="true">
                <span className="ngf-toggle__knob" />
              </span>
            </label>
          </div>
        </div>

        {/* ── CONTINUATION: алдыңғы топты таңдау ── */}
        {isContinuation && (
          <div className="ngf-group">
            <label className="ngf-label">{t.parentGroupLabel}</label>
            <p className="ngf-hint">{t.parentGroupHint}</p>

            {completedLoading ? (
              <p className="ngf-hint">{t.parentGroupLoading}</p>
            ) : completedGroups.length === 0 ? (
              <p className="ngf-hint" style={{ color: '#e74c3c' }}>
                {t.parentGroupEmpty}
              </p>
            ) : (
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  value={form.parent_group_id}
                  onChange={(e) => handleParentGroupSelect(e.target.value)}
                  required
                >
                  <option value="">{t.parentGroup}</option>
                  {completedGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.title}
                      {group.subject?.['name_' + locale]
                        ? ` — ${group.subject['name_' + locale]}`
                        : ''}
                      {group.grade?.['name_' + locale]
                        ? `, ${group.grade['name_' + locale]}`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Таңдалған топтың деректері */}
            {selectedParentGroup && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                }}
              >
                <p style={{ margin: 0, fontWeight: 600, color: '#166534' }}>
                  {t.copiedInfo}
                </p>
                <ul style={{ margin: '0.5rem 0 0 1rem', color: '#15803d' }}>
                  {selectedParentGroup.subject?.['name_' + locale] && (
                    <li>{t.subject}: {selectedParentGroup.subject['name_' + locale]}</li>
                  )}
                  {selectedParentGroup.grade?.['name_' + locale] && (
                    <li>{t.grade}: {selectedParentGroup.grade['name_' + locale]}</li>
                  )}
                  {selectedParentGroup.education_level?.['name_' + locale] && (
                    <li>{t.educationLevel}: {selectedParentGroup.education_level['name_' + locale]}</li>
                  )}
                  {selectedParentGroup.categories?.length > 0 && (
                    <li>
                      {t.categories}:{' '}
                      {selectedParentGroup.categories
                        .map((c) => c['name_' + locale] || c.name_kz)
                        .join(', ')}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ── NEW режімі: барлық өрістер ── */}
        {!isContinuation && (
          <div className="ngf-grid">
            <div className="form-group ngf-field">
              <label className="ngf-label">{t.educationLevel}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="education_level_id"
                  value={form.education_level_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.education_levels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level[`name_${locale}`] || level.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.subject}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="subject_id"
                  value={form.subject_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject[`name_${locale}`] || subject.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.grade}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="grade_id"
                  value={form.grade_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.grades.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.direction}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="direction_id"
                  value={form.direction_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.directions.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.devLang}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="development_language_id"
                  value={form.development_language_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.languages.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.teachLang}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="teaching_language_id"
                  value={form.teaching_language_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.languages.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.publicationType}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="publication_type_id"
                  value={form.publication_type_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.publication_types.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.serviceType}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="service_type_id"
                  value={form.service_type_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.service_types.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group ngf-field">
              <label className="ngf-label">{t.submissionYear}</label>
              <div className="ngf-select">
                <select
                  className="ngf-select__control"
                  name="submission_year_id"
                  value={form.submission_year_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t.select}</option>
                  {refs.years.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x[`name_${locale}`] || x.year || x.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── Categories ── */}
        <div className="form-group ngf-cat">
          <label className="ngf-label">{t.categories}</label>

          {isContinuation && selectedParentGroup ? (
            // Continuation: категориялар тек көрсетіледі, өзгертілмейді
            <div className="ngf-cat__list ngf-cat__list--scroll">
              {(selectedParentGroup.categories || []).map((category) => (
                <label
                  key={category.id}
                  className="ngf-chip is-checked is-locked"
                  title={category[`name_${locale}`] || category.name_kz}
                >
                  <input type="checkbox" className="ngf-chip__input" checked disabled readOnly />
                  <span className="ngf-chip__text">
                    {category[`name_${locale}`] || category.name_kz}
                  </span>
                </label>
              ))}
              {(selectedParentGroup.categories || []).length === 0 && (
                <div className="ngf-empty">{t.nothingFound}</div>
              )}
            </div>
          ) : (
            // New: категорияларды таңдауға болады
            <>
              <div className="ngf-cat__toolbar">
                <input
                  className="ngf-input"
                  type="text"
                  value={catQ}
                  onChange={(e) => setCatQ(e.target.value)}
                  placeholder={t.searchCat}
                />

                <label className="ngf-switch ngf-switch--sm">
                  <input
                    type="checkbox"
                    checked={showSelectedOnly}
                    onChange={(e) => setShowSelectedOnly(e.target.checked)}
                  />
                  <span className="ngf-switch__slider" />
                  <span className="ngf-switch__text">{t.onlySelected}</span>
                </label>

                {!isPreSchool && (
                  <>
                    <button
                      type="button"
                      className="ngf-btn ngf-btn--ghost"
                      onClick={() => {
                        const allVisible = refs.categories
                          .filter((c) => {
                            const name = (c[`name_${locale}`] || c.name || '').toLowerCase();
                            return !catQ || name.includes(catQ.toLowerCase());
                          })
                          .map((c) => String(c.id));
                        setForm((p) => ({ ...p, category_ids: allVisible }));
                      }}
                    >
                      {t.chooseAll}
                    </button>

                    <button
                      type="button"
                      className="ngf-btn"
                      onClick={() => setForm((p) => ({ ...p, category_ids: [] }))}
                    >
                      {t.clear}
                    </button>
                  </>
                )}

                <span className="ngf-cat__count">
                  {t.chosen} {form.category_ids.length}
                </span>
              </div>

              <div className="ngf-cat__list ngf-cat__list--scroll">
                {refs.categories
                  .filter((cat) => !isPreSchool || cat.id === 2)
                  .filter((cat) => {
                    const name = (cat[`name_${locale}`] || cat.name || '').toLowerCase();
                    const matches = !catQ || name.includes(catQ.toLowerCase());
                    const idStr = String(cat.id);
                    const chosen = form.category_ids.includes(idStr);
                    return matches && (!showSelectedOnly || chosen);
                  })
                  .map((category) => {
                    const idStr = String(category.id);
                    const isChecked = form.category_ids.includes(idStr);
                    return (
                      <label
                        key={category.id}
                        className={`ngf-chip ${isChecked ? 'is-checked' : ''} ${
                          isPreSchool ? 'is-locked' : ''
                        }`}
                        title={category[`name_${locale}`] || category.name}
                      >
                        <input
                          type="checkbox"
                          className="ngf-chip__input"
                          value={category.id}
                          checked={isChecked}
                          disabled={isPreSchool}
                          onChange={handleCheckboxChange}
                        />
                        <span className="ngf-chip__text">
                          {category[`name_${locale}`] || category.name}
                        </span>
                      </label>
                    );
                  })}

                {refs.categories
                  .filter((cat) => !isPreSchool || cat.id === 2)
                  .filter((cat) => {
                    const name = (cat[`name_${locale}`] || cat.name || '').toLowerCase();
                    const matches = !catQ || name.includes(catQ.toLowerCase());
                    const idStr = String(cat.id);
                    const chosen = form.category_ids.includes(idStr);
                    return matches && (!showSelectedOnly || chosen);
                  }).length === 0 && (
                  <div className="ngf-empty">{t.nothingFound}</div>
                )}
              </div>
            </>
          )}

          {info && !isContinuation && (
            <p className="ngf-help ngf-help--warn">⚠️ {info}</p>
          )}
        </div>

        {/* ── Атау мен сипаттама ── */}
        <div className="ngf-wide">
          <div className="form-group">
            <label>{t.groupName}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                name="title"
                value={form.title}
                onChange={handleTitleChange}
                placeholder={t.nameExample}
                required
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="ngf-btn ngf-btn--ghost"
                onClick={resetTitleToAuto}
                title={t.autoHint}
              >
                ↺ {t.autoFill}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>{t.description}</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t.descriptionPlaceholder}
            />
          </div>
        </div>

        <div className="ngf-actions">
          <button
            type="submit"
            className="ngf-btn ngf-btn--primary ngf-btn--sm"
            disabled={loading || (isContinuation && !form.parent_group_id)}
            title={t.create}
          >
            <svg className="ngf-btn__icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 5v14M5 12h14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="ngf-btn__text">
              {loading ? '...' : t.create}
            </span>
          </button>
        </div>

        {(success || error) && (
          <div
            className={`ngf-toast ngf-toast--xl ${success ? 'is-success' : 'is-error'}`}
            role="alertdialog"
            aria-live="assertive"
            onClick={dismissToast}
          >
            <div className="ngf-toast__card" onClick={(e) => e.stopPropagation()}>
              <div className="ngf-toast__iconwrap">
                {success ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                )}
              </div>
              <div className="ngf-toast__body">
                <div className="ngf-toast__title">{success ? t.successTitle : t.errorTitle}</div>
                <div className="ngf-toast__text">{success || error}</div>
              </div>
              <button className="ngf-toast__close" onClick={dismissToast} aria-label="Close">×</button>
              <div className="ngf-toast__progress" />
            </div>
          </div>
        )}
      </form>
    </div>
  );
}